/* === MAP_SECTION START === */
import React, { useState, useEffect, useMemo } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Circle,
  LayerGroup,
  LayersControl,
  useMapEvents,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useTileCache } from "../hooks/useTileCache";
import CachedTileLayer from "./CachedTileLayer";
import {
  HGM_ATLAS_ATTRIBUTION,
  buildHgmAtlasSatelliteUrls,
  buildHgmAtlasTileUrl,
  isHgmAtlasConfigured,
  reverseGeocodeLocation,
} from "../services/hgmAtlasService";
import {
  ICON_COLORS,
  getIconForStatus,
  getSampleEmoji,
  getStatusLabel,
  hasPointDataEntryForLabel,
  makeDivIcon,
  printTwoLabels,
} from "./mapSectionUtils";

const HGM_ATLAS_TILES = isHgmAtlasConfigured
  ? {
      uydu: buildHgmAtlasSatelliteUrls(),
      temel: buildHgmAtlasTileUrl("temel"),
      yukseklik: buildHgmAtlasTileUrl("yukseklik"),
      siyasi: buildHgmAtlasTileUrl("siyasi"),
      gece: buildHgmAtlasTileUrl("gece"),
    }
  : null;

function LocationClick({
  setClickedInfo,
  moveModeId,
  setTempPosition,
  setClickMarker,
  showVeriPopup,
}) {
  useMapEvents({
    click: async (e) => {
      // Veri girişi popup açıksa, haritaya tıklayınca koordinat değişmesin.
      if (showVeriPopup) {
        return;
      }

      const { lat, lng } = e.latlng;

      if (moveModeId) {
        setTempPosition({ lat: +lat.toFixed(6), lng: +lng.toFixed(6) });
        return;
      }

      setClickMarker({ lat: +lat.toFixed(6), lng: +lng.toFixed(6) });

      let locationName = "Bilinmeyen konum";
      try {
        const address = await reverseGeocodeLocation(lat, lng);
        locationName = address.displayName;
      } catch {
        locationName = "Adres sorgusu başarısız";
      }

      setClickedInfo({
        lat: +lat.toFixed(6),
        lng: +lng.toFixed(6),
        locationName,
      });
    },
  });
  return null;
}

function ZoomWatcher({ setMapZoom }) {
  useMapEvents({
    zoomend: (e) => {
      const z = e.target.getZoom();
      setMapZoom(z);
    },
  });
  return null;
}

function GpsRecenterControl({ gpsPosition }) {
  const map = useMap();

  const handleRecenter = (e) => {
    e.stopPropagation();
      const hasLat = Number.isFinite(gpsPosition?.lat);
      const hasLng = Number.isFinite(gpsPosition?.lng);
      if (hasLat && hasLng) {
        const accuracy = Number(gpsPosition?.accuracy);
        const accuracyBasedZoom = !Number.isFinite(accuracy)
          ? 16
          : accuracy <= 10
            ? 18
            : accuracy <= 20
              ? 17
              : accuracy <= 40
                ? 16
                : 15;
        map.flyTo([gpsPosition.lat, gpsPosition.lng], accuracyBasedZoom, {
          animate: true,
          duration: 1,
        });
    } else {
      const reason =
        typeof gpsPosition?.errorMessage === "string" && gpsPosition.errorMessage.trim()
          ? gpsPosition.errorMessage
          : "GPS konumu henuz alinamadi veya kapali.";
      alert(reason);
    }
  };

  return (
    <button
      onClick={handleRecenter}
      title="GPS Konumuma Git"
      style={{
        position: "absolute",
        bottom: "80px", // Avoid overlap with bottom controls
        right: "10px",
        zIndex: 1000,
        backgroundColor: "white",
        border: "2px solid rgba(0,0,0,0.2)",
        borderRadius: "8px",
        width: "44px",
        height: "44px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
        fontSize: "12px",
        fontWeight: "bold",
        color: "#2563eb",
      }}
    >
      GPS
    </button>
  );
}

function ZoomToMarkerButton({ lat, lng }) {
  const map = useMap();

  const handleZoomToMarker = () => {
    if (lat == null || lng == null) return;

    const rawMaxZoom =
      typeof map.getMaxZoom === "function" ? map.getMaxZoom() : null;
    const targetZoom =
      Number.isFinite(rawMaxZoom) && rawMaxZoom > 0 ? rawMaxZoom : 19;

    map.flyTo([lat, lng], targetZoom, { animate: true, duration: 0.8 });
  };

  return (
    <button
      style={{
        padding: "6px 8px",
        backgroundColor: "#0ea5e9",
        color: "#fff",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontSize: "12px",
        fontWeight: 700,
        minHeight: 34,
        width: "100%",
      }}
      onClick={handleZoomToMarker}
      title="Noktaya maksimum yakınlaş"
    >
      Zoom
    </button>
  );
}

function CoordinateSearchFocus({ target }) {
  const map = useMap();

  useEffect(() => {
    if (!Number.isFinite(target?.lat) || !Number.isFinite(target?.lng)) {
      return;
    }

    map.flyTo([target.lat, target.lng], 17, {
      animate: true,
      duration: 0.8,
    });
  }, [map, target?.id, target?.lat, target?.lng]);

  return null;
}

export default function MapSection(props) {
  const [mapZoom, setMapZoom] = useState(12);
  const {
    gpsPosition,
    clickMarker,
    setClickMarker,
    moveModeId,
    tempPosition,
    setTempPosition,
    confirmMoveToTemp,
    cancelMove,
    visiblePoints,
    points: allPoints,
    selectedProjectId,
    paftalar,
    teams,
    changePointStatus,
    setClickedInfo,
    setSicaklik,
    setAtm,
    setSpc,
    setTds,
    setSal,
    setPh,
    // Not alanını kontrol etmek için
    setNotMetni,
    setShowVeriPopup, assignTeamToPoint,
    startMoveMode,
    selectedPaftaForTeams,
    setSelectedPaftaForTeams,
    deleteTeam,
    handleDeleteMember,
    selectedTeamId,
    showVeriPopup,
    createPointLocalFirst,
    canManageTeams,
    coordinateSearchTarget,
  } = props;
  const {
    prefetchPoints,
    syncProjectActivePaftaCache,
    isPrefetching
  } = useTileCache();

  const activePaftaIds = useMemo(
    () =>
      (paftalar || [])
        .filter((pafta) => pafta.aktif)
        .map((pafta) => String(pafta.id)),
    [paftalar]
  );

  const visibleActivePaftaIds = useMemo(
    () =>
      (paftalar || [])
        .filter((pafta) => pafta.aktif && pafta.gorunur)
        .map((pafta) => String(pafta.id)),
    [paftalar]
  );

  const activePointsForPrefetch = useMemo(() => {
    if (!allPoints?.length || !activePaftaIds.length) return [];
    const activeSet = new Set(activePaftaIds);
    return allPoints.filter((point) => activeSet.has(String(point.paftaId || "")));
  }, [activePaftaIds, allPoints]);

  useEffect(() => {
    if (!clickMarker) return undefined;

    const timer = setTimeout(() => {
      setClickMarker(null);
    }, 5000);

    return () => clearTimeout(timer);
  }, [clickMarker, setClickMarker]);

  useEffect(() => {
    if (!selectedProjectId) return;

    syncProjectActivePaftaCache(selectedProjectId, activePaftaIds).catch((error) => {
      console.warn("Pafta cache temizliği yapılamadı:", error);
    });
  }, [activePaftaIds, selectedProjectId, syncProjectActivePaftaCache]);

  // Aktif paftalar/noktalar değiştiğinde çevresini arka planda indir
  useEffect(() => {
    if (selectedProjectId && activePointsForPrefetch && activePointsForPrefetch.length > 0) {
      // Çok sık tetiklenmemesi için kısa bir gecikme ile çalıştır
      const timer = setTimeout(() => {
        const tileUrl = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
        prefetchPoints(activePointsForPrefetch, 2, 12, 17, tileUrl, selectedProjectId);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [activePointsForPrefetch, prefetchPoints, selectedProjectId]);

  const selectedPaftaTeams = useMemo(() => {
    if (!selectedPaftaForTeams) return [];
    return (teams || []).filter((team) => team.paftaId === selectedPaftaForTeams);
  }, [selectedPaftaForTeams, teams]);

  const selectedPaftaName = useMemo(() => {
    if (!selectedPaftaForTeams) return "Pafta";
    return (
      paftalar.find((pafta) => pafta.id === selectedPaftaForTeams)?.isim || "Pafta"
    );
  }, [paftalar, selectedPaftaForTeams]);

  return (
    <>

      <MapContainer
        center={[39.93, 32.85]}
        zoom={12}
        zoomControl={false}
        style={{ height: "100vh", width: "100%", backgroundColor: "#d6e3ef" }}
      >
        <LayersControl position="topright">
          {HGM_ATLAS_TILES ? (
            <>
              <LayersControl.BaseLayer checked name="HGM Atlas Uydu">
                <LayerGroup>
                  {HGM_ATLAS_TILES.uydu.map((url) => (
                    <TileLayer
                      key={url}
                      url={url}
                      maxZoom={19}
                      attribution={HGM_ATLAS_ATTRIBUTION}
                    />
                  ))}
                </LayerGroup>
              </LayersControl.BaseLayer>

              <LayersControl.BaseLayer name="HGM Atlas Temel">
                <TileLayer
                  url={HGM_ATLAS_TILES.temel}
                  maxZoom={19}
                  attribution={HGM_ATLAS_ATTRIBUTION}
                />
              </LayersControl.BaseLayer>

              <LayersControl.BaseLayer name="HGM Atlas Yükseklik">
                <TileLayer
                  url={HGM_ATLAS_TILES.yukseklik}
                  maxZoom={19}
                  attribution={HGM_ATLAS_ATTRIBUTION}
                />
              </LayersControl.BaseLayer>

              <LayersControl.BaseLayer name="HGM Atlas Siyasi">
                <TileLayer
                  url={HGM_ATLAS_TILES.siyasi}
                  maxZoom={19}
                  attribution={HGM_ATLAS_ATTRIBUTION}
                />
              </LayersControl.BaseLayer>

              <LayersControl.BaseLayer name="HGM Atlas Gece">
                <TileLayer
                  url={HGM_ATLAS_TILES.gece}
                  maxZoom={19}
                  attribution={HGM_ATLAS_ATTRIBUTION}
                />
              </LayersControl.BaseLayer>

              <LayersControl.BaseLayer name="Çevrimdışı Uydu (Mevcut)">
                <CachedTileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  maxZoom={19}
                  projectId={selectedProjectId}
                  paftaIds={visibleActivePaftaIds}
                />
              </LayersControl.BaseLayer>
            </>
          ) : (
            <>
              <LayersControl.BaseLayer checked name="Uydu (Esri)">
                <CachedTileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  maxZoom={19}
                  projectId={selectedProjectId}
                  paftaIds={visibleActivePaftaIds}
                />
              </LayersControl.BaseLayer>

              <LayersControl.BaseLayer name="Topo">
                <TileLayer url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png" />
              </LayersControl.BaseLayer>

              <LayersControl.BaseLayer name="Standart">
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              </LayersControl.BaseLayer>
            </>
          )}
        </LayersControl>

        {/* Zoom'u dinleyip mapZoom state'ini güncelleyen bileşen */}
        <ZoomWatcher setMapZoom={setMapZoom} />

        {/* GPS'e Dönme Butonu */}
        <GpsRecenterControl gpsPosition={gpsPosition} />


        <LocationClick
          setClickedInfo={setClickedInfo}
          moveModeId={moveModeId}
          setTempPosition={setTempPosition}
          setClickMarker={setClickMarker}
          showVeriPopup={showVeriPopup}
        />

        <CoordinateSearchFocus target={coordinateSearchTarget} />
        {Number.isFinite(coordinateSearchTarget?.lat) &&
        Number.isFinite(coordinateSearchTarget?.lng) ? (
          <Marker
            position={[coordinateSearchTarget.lat, coordinateSearchTarget.lng]}
            icon={makeDivIcon({
              color: "#0f766e",
              border: "#ccfbf1",
              size: 22,
              label: "ARA",
            })}
          >
            <Popup closeButton={false}>
              <b>Koordinat Arama</b>
              <div>
                X: {coordinateSearchTarget.lng.toFixed(6)}, Y:{" "}
                {coordinateSearchTarget.lat.toFixed(6)}
              </div>
            </Popup>
          </Marker>
        ) : null}

        {Number.isFinite(gpsPosition?.lat) && Number.isFinite(gpsPosition?.lng) ? (
          <>
            {Number.isFinite(gpsPosition?.accuracy) ? (
              <Circle
                center={[gpsPosition.lat, gpsPosition.lng]}
                radius={Math.max(5, gpsPosition.accuracy)}
                pathOptions={{
                  color: "#2563eb",
                  weight: 1.5,
                  fillColor: "#3b82f6",
                  fillOpacity: 0.12,
                }}
              />
            ) : null}

            <Marker
              position={[gpsPosition.lat, gpsPosition.lng]}
              icon={makeDivIcon({ color: ICON_COLORS.gps })}
            >
              <Popup closeButton={false}>
                <b>Mevcut Konum</b>
                <div>
                  {gpsPosition.lat.toFixed(6)}, {gpsPosition.lng.toFixed(6)}
                </div>
                {Number.isFinite(gpsPosition?.accuracy) ? (
                  <div>GPS dogruluk: +/- {gpsPosition.accuracy} m</div>
                ) : null}
                {Number.isFinite(gpsPosition?.rawAccuracy) ? (
                  <div>Anlik olcum: +/- {gpsPosition.rawAccuracy} m</div>
                ) : null}
                {Number.isFinite(gpsPosition?.sampleCount) ? (
                  <div>Ortalama ornek: {gpsPosition.sampleCount}</div>
                ) : null}
              </Popup>
            </Marker>
          </>
        ) : null}

        {/* Yeni Nokta Ekleme Marker */}
        {clickMarker && (
          <Marker
            position={[clickMarker.lat, clickMarker.lng]}
            icon={makeDivIcon({
              color: ICON_COLORS.temp,
              border: "#0078ff",
              size: 10,
            })}
            eventHandlers={{
              popupclose: () => setClickMarker(null),
            }}
          >
            <Popup autoClose closeOnClick closeButton={false}>
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    marginBottom: 6,
                    fontWeight: "bold",
                  }}
                >
                  Yeni Nokta Ekle
                </div>

                <input
                  id="newPointName"
                  type="text"
                  placeholder="Nokta adını giriniz"
                  style={{
                    width: "100%",
                    padding: "4px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                    marginBottom: 8,
                  }}
                />

                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  <button
                    style={{
                      background: "#10b981",
                      color: "#fff",
                      border: "none",
                      padding: "4px 8px",
                      borderRadius: 4,
                      cursor: "pointer",
                    }}
                    onClick={async () => {
                      const input = document.getElementById("newPointName");
                      const name = input?.value?.trim();

                      if (!name) {
                        alert("Lütfen nokta adını giriniz.");
                        return;
                      }

                      const aktifPaftalar = paftalar.filter((p) => p.gorunur);
                      if (aktifPaftalar.length !== 1) {
                        alert(
                          "Lütfen yalnızca bir pafta seçiliyken nokta ekleyin!"
                        );
                        return;
                      }

                      const aktifPafta = aktifPaftalar[0];
                      const paftaTeams = (teams || []).filter(
                        (t) => t.paftaId === aktifPafta.id
                      );

                      if (
                        paftaTeams.length > 0 &&
                        (!selectedTeamId ||
                          !paftaTeams.some((t) => t.id === selectedTeamId))
                      ) {
                        alert(
                          "Bu paftada tanımlı ekip(ler) var. Lütfen bir ekip seçerek noktayı ekleyin."
                        );
                        return;
                      }

                      const finalEkipId =
                        paftaTeams.length === 0 ? null : selectedTeamId;

                      try {
                        await createPointLocalFirst({
                          paftaId: aktifPafta.id,
                          lat: clickMarker.lat,
                          lng: clickMarker.lng,
                          name,
                          ekipId: finalEkipId,
                        });

                        setClickMarker(null);
                      } catch (err) {
                        console.error("Yeni nokta eklenemedi:", err);
                        alert("Yeni nokta kaydedilemedi.");
                      }
                    }}
                  >
                    Ekle
                  </button>

                  <button
                    style={{
                      background: "#ef4444",
                      color: "#fff",
                      border: "none",
                      padding: "4px 8px",
                      borderRadius: 4,
                      cursor: "pointer",
                    }}
                    onClick={() => setClickMarker(null)}
                  >
                    İptal
                  </button>
                </div>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Taşıma Modu Geçici Marker */}
        {tempPosition && (
          <Marker
            position={[tempPosition.lat, tempPosition.lng]}
            icon={makeDivIcon({
              color: ICON_COLORS.temp,
              border: "#0078ff",
            })}
          >
            <Popup closeButton={false}>
              <div style={{ minWidth: 180 }}>
                <div style={{ marginBottom: 8 }}>
                  Bu konuma taşımak istiyor musunuz?
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={confirmMoveToTemp}
                    style={{
                      background: "#10b981",
                      color: "#fff",
                      border: "none",
                      padding: "6px 8px",
                      borderRadius: 6,
                      cursor: "pointer",
                    }}
                  >
                    Evet
                  </button>

                  <button
                    onClick={cancelMove}
                    style={{
                      background: "#ef4444",
                      color: "#fff",
                      border: "none",
                      padding: "6px 8px",
                      borderRadius: 6,
                      cursor: "pointer",
                    }}
                  >
                    Hayır
                  </button>
                </div>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Nokta katmanı */}
        {visiblePoints.map((pt) => {
          const lat = pt.lat ?? null;  // Y
          const lng = pt.lng ?? null;  // X

          if (lat == null || lng == null) return null;


          // === LABEL_CAN_PRINT_LOGIC_START ===
          const statusOk = ["Pozitif", "Konum Güncellendi", "Yeni"].includes(
            pt.status
          );

          // Etiket için tüm ölçüm alanlarının dolması gerekmez; veri girişi yapılmış olması yeterlidir.
          const dataOk = hasPointDataEntryForLabel(pt);

          const canPrint = statusOk && dataOk;
          const sampleEmoji = getSampleEmoji(pt);
          // === LABEL_CAN_PRINT_LOGIC_END ===

          return (
            <Marker
              key={`${pt.paftaId}-${pt.id}`}
              position={[lat, lng]}
              icon={getIconForStatus(pt.status, pt.name, mapZoom, sampleEmoji)}
            >
              <Popup>
                <div
                  style={{
                    minWidth: 260,
                    maxWidth: 320,
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    lineHeight: 1.25,
                  }}
                >
                  <b
                    style={{
                      display: "block",
                      marginBottom: 2,
                      fontSize: 18,
                    }}
                  >
                    {pt.name} ({pt.pafta})
                  </b>

                  <div>
                    Durum:{" "}
                    <span
                      style={{
                        fontWeight: "bold",
                        color:
                          ICON_COLORS[pt.status] || ICON_COLORS.pending,
                      }}
                    >
                      {getStatusLabel(pt.status)}
                    </span>
                  </div>

                  <div>
                    Ekip:{" "}
                    <b>
                      {teams.find((t) => t.id === pt.ekipId)?.name ||
                        "Atanmamış"}
                    </b>
                  </div>

                  <div>
                    Numune Türü:{" "}
                    <b>
                      {pt.numuneTuru ||
                        pt.numuneTuruAdi ||
                        pt.customMeasurements?.["Numune Türü"] ||
                        pt.customMeasurements?.numuneTuru ||
                        "Belirtilmedi"}
                    </b>
                  </div>

                  {/* === LABEL_MARKER_ACTIONS_START === */}
                  <div
                    style={{
                      marginTop: 2,
                      display: "grid",
                      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                      gap: 6,
                    }}
                  >
                    <ZoomToMarkerButton lat={lat} lng={lng} />
                    <button
                      style={{
                        padding: "6px 8px",
                        backgroundColor: canPrint ? "#10b981" : "#9ca3af",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: canPrint ? "pointer" : "not-allowed",
                        fontSize: "12px",
                        fontWeight: 700,
                        minHeight: 34,
                        width: "100%",
                      }}
                      disabled={!canPrint}
                      onClick={() => {
                        const yil = new Date().getFullYear().toString().slice(-2);

                        // Pafta adından klasör yolunu ve uzantısını temizle
                        const rawPafta = pt.pafta || "";
                        const fileNameOnly = rawPafta.split(/[\\/]/).pop(); // sadece dosya adı
                        const paftaLabel = fileNameOnly.replace(/\.(kmz|kml)$/i, "");
                        const noktaAdi =
                          String(pt.name ?? "").trim() || "NOKTA";

                        const kod = `${yil}-${paftaLabel || "PAFTA"}-${noktaAdi}`;
                        printTwoLabels(kod);
                      }}

                    >
                      Etiket Yazdır
                    </button>

                    <button
                      style={{
                        padding: "6px 8px",
                        backgroundColor: "#007bff",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: 700,
                        minHeight: 34,
                        width: "100%",
                      }}
                      onClick={() => {
                        const selectedCount = paftalar.filter(
                          (p) => p.gorunur
                        ).length;
                        if (selectedCount > 1) {
                          alert(
                            "Uyari: Veri girişi sadece tek pafta açıkken yapılabilir."
                          );
                          return;
                        }

                        // Artık tek şema: her noktada sadece lat / lng var.
                        const lat = pt.lat ?? null; // Y
                        const lng = pt.lng ?? null; // X

                        setClickedInfo({
                          lat,
                          lng,
                          name: pt.name,
                          id: pt.id,
                          paftaId: pt.paftaId,
                        });

                        setSicaklik(pt.sicaklik ?? "");
                        setAtm(pt.atm ?? "");
                        setSpc(pt.spc ?? "");
                        setTds(pt.tds ?? "");
                        setSal(pt.sal ?? "");
                        setPh(pt.ph ?? "");
                        setNotMetni(pt.notMetni ?? "");

                        setShowVeriPopup(true);
                      }}
                    >
                      Veri Girişi
                    </button>
                  </div>
                  {/* === LABEL_MARKER_ACTIONS_END === */}

                  {(() => {
                    const paftaIdForPoint =
                      pt.paftaId ||
                      (paftalar.find((pf) => pf.isim === pt.pafta)?.id ??
                        null);

                    const paftaTeams = (teams || []).filter(
                      (team) => team.paftaId === paftaIdForPoint
                    );

                    const selectedEkipId =
                      paftaTeams.some((t) => t.id === pt.ekipId) &&
                        pt.ekipId
                        ? pt.ekipId
                        : "null";

                    return (
                      <select
                        value={selectedEkipId}
                        onChange={(e) =>
                          assignTeamToPoint(
                            paftaIdForPoint,
                            pt.id,
                            e.target.value === "null"
                              ? null
                              : e.target.value
                          )
                        }
                        style={{
                          width: "100%",
                          marginTop: 2,
                          padding: "8px 10px",
                          borderRadius: 4,
                          border: "1px solid #ccc",
                          fontSize: 14,
                        }}
                      >
                        <option value="null">
                          Ekip Atanmadı (Temizle)
                        </option>

                        {paftaTeams.map((team) => (
                          <option key={team.id} value={team.id}>
                            {team.name}
                          </option>
                        ))}
                      </select>
                    );
                  })()}

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                      gap: 6,
                      marginTop: 2,
                    }}
                  >
                    {["Pozitif", "Negatif", "Tekrar", "pending"].map(
                      (status) => (
                        <button
                          key={status}
                          onClick={() =>
                            changePointStatus(
                              pt.id,
                              pt.paftaId,
                              status
                            )
                          }
                          disabled={pt.status === status}
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            padding: "6px 6px",
                            border: "1px solid #ccc",
                            borderRadius: 4,
                            background: ICON_COLORS[status],
                            color: "#fff",
                            cursor: pt.status === status ? "default" : "pointer",
                            opacity: 1,
                          }}
                        >
                          {getStatusLabel(status)}
                        </button>
                      )
                    )}
                  </div>

                  <button
                    onClick={() => startMoveMode(pt.id, pt.paftaId)}
                    style={{
                      background: "#ffc107",
                      color: "#333",
                      border: "none",
                      marginTop: 2,
                      padding: "8px 10px",
                      borderRadius: 6,
                      cursor: "pointer",
                      width: "100%",
                      fontSize: 16,
                      fontWeight: 700,
                    }}
                  >
                    Konum Kaydır
                  </button>
                </div>
              </Popup>
            </Marker>
          );
        })}
        {/* === PAFTA TEAM PANEL START === */}
        {selectedPaftaForTeams ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 12,
              background: "rgba(2, 18, 46, 0.42)",
              backdropFilter: "blur(6px)",
            }}
            onClick={() => setSelectedPaftaForTeams(null)}
          >
            <div
              style={{
                width: "min(420px, 92vw)",
                maxHeight: "72vh",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                borderRadius: 16,
                border: "1px solid rgba(96, 165, 250, 0.45)",
                background:
                  "linear-gradient(160deg, #f8fbff 0%, #edf5ff 58%, #e8f8f2 100%)",
                boxShadow: "0 28px 60px rgba(2, 6, 23, 0.45)",
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  padding: "12px 14px",
                  borderBottom: "1px solid rgba(125, 211, 252, 0.45)",
                  background:
                    "linear-gradient(135deg, rgba(15,118,110,0.92) 0%, rgba(29,78,216,0.9) 55%, rgba(124,58,237,0.9) 100%)",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "0.74rem",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      fontWeight: 700,
                      color: "rgba(226,232,240,0.95)",
                    }}
                  >
                    Takım Görünümü
                  </div>
                  <div
                    style={{
                      marginTop: 2,
                      fontSize: "0.98rem",
                      fontWeight: 700,
                      color: "#ffffff",
                    }}
                  >
                    {selectedPaftaName}
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      fontSize: "0.74rem",
                      fontWeight: 700,
                      color: "#ffffff",
                      background: "rgba(255,255,255,0.16)",
                      border: "1px solid rgba(255,255,255,0.42)",
                      borderRadius: 999,
                      padding: "3px 8px",
                    }}
                  >
                    {selectedPaftaTeams.length} takım
                  </span>
                  <button
                    onClick={() => setSelectedPaftaForTeams(null)}
                    style={{
                      border: "1px solid rgba(255,255,255,0.5)",
                      background: "rgba(255,255,255,0.16)",
                      color: "#ffffff",
                      borderRadius: 999,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      padding: "4px 10px",
                      cursor: "pointer",
                    }}
                  >
                    Kapat
                  </button>
                </div>
              </div>

              <div
                style={{
                  padding: 10,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  background:
                    "linear-gradient(180deg, rgba(248,250,252,0.72) 0%, rgba(240,249,255,0.62) 100%)",
                }}
              >
                {selectedPaftaTeams.length === 0 ? (
                  <div
                    style={{
                      padding: "18px 12px",
                      borderRadius: 12,
                      border: "1px dashed rgba(59,130,246,0.55)",
                      color: "#1e3a8a",
                      textAlign: "center",
                      fontSize: "0.85rem",
                      background: "rgba(239,246,255,0.78)",
                    }}
                  >
                    Bu paftaya ait takım bulunmuyor.
                  </div>
                ) : (
                  selectedPaftaTeams.map((team) => {
                    const members = Array.isArray(team.members) ? team.members : [];

                    return (
                      <div
                        key={team.id}
                        style={{
                          borderRadius: 12,
                          border: "1px solid rgba(96,165,250,0.38)",
                          background:
                            "linear-gradient(145deg, rgba(255,255,255,0.96) 0%, rgba(239,246,255,0.92) 100%)",
                          boxShadow: "0 8px 20px rgba(30, 64, 175, 0.12)",
                          padding: "10px 10px 8px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                            marginBottom: 6,
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: "0.92rem",
                                fontWeight: 700,
                                color: "#0b3a72",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {team.name}
                            </div>
                            <div
                              style={{
                                marginTop: 2,
                                fontSize: "0.74rem",
                                color: "#0f766e",
                                fontWeight: 600,
                              }}
                            >
                              {members.length} üye
                            </div>
                          </div>

                          {canManageTeams ? (
                            <button
                              onClick={() => deleteTeam(team.id, team.name)}
                              style={{
                                border: "1px solid rgba(248,113,113,0.52)",
                                background:
                                  "linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)",
                                color: "#be123c",
                                borderRadius: 999,
                                fontSize: "0.72rem",
                                fontWeight: 700,
                                padding: "3px 8px",
                                cursor: "pointer",
                                flexShrink: 0,
                              }}
                            >
                              Sil
                            </button>
                          ) : null}
                        </div>

                        {members.length === 0 ? (
                          <div
                            style={{
                              fontSize: "0.79rem",
                              color: "#1e3a8a",
                              background: "rgba(219,234,254,0.55)",
                              borderRadius: 8,
                              padding: "6px 8px",
                            }}
                          >
                            Üye yok
                          </div>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 4,
                            }}
                          >
                            {members.map((member, memberIndex) => (
                              <div
                                key={`${team.id}_${memberIndex}`}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 6,
                                  padding: "5px 7px",
                                  borderRadius: 8,
                                  border: "1px solid rgba(125,211,252,0.55)",
                                  background:
                                    memberIndex % 2 === 0
                                      ? "rgba(239,246,255,0.95)"
                                      : "rgba(240,253,250,0.95)",
                                }}
                              >
                                <span
                                    style={{
                                      fontSize: "0.79rem",
                                      color: "#0f172a",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                  }}
                                >
                                  {memberIndex + 1}. {member}
                                </span>

                                {canManageTeams ? (
                                  <button
                                    onClick={() => handleDeleteMember(team.id, memberIndex)}
                                    style={{
                                      border: "1px solid rgba(248,113,113,0.5)",
                                      background: "#fff5f5",
                                      color: "#be123c",
                                      borderRadius: 999,
                                      fontSize: "0.72rem",
                                      fontWeight: 700,
                                      padding: "1px 8px",
                                      cursor: "pointer",
                                      flexShrink: 0,
                                    }}
                                  >
                                    X
                                  </button>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        ) : null}
        {/* === PAFTA TEAM PANEL END === */}
      </MapContainer>

      {!isHgmAtlasConfigured ? (
        <div
          style={{
            position: "absolute",
            top: 12,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            background: "rgba(127, 29, 29, 0.92)",
            color: "white",
            padding: "7px 12px",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 700,
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          }}
        >
          HGM Atlas anahtarı tanımlı değil; mevcut harita kullanılıyor.
        </div>
      ) : null}

      {/* --- ARKA PLAN ÖNBELLEKLEME GÖSTERGESİ (Sessiz) --- */}
      {isPrefetching && (
        <div style={{
          position: "absolute", bottom: 20, right: 100, zIndex: 9999,
          background: "rgba(37, 99, 235, 0.9)", color: "white", padding: "6px 12px",
          borderRadius: "20px", fontSize: "11px", fontWeight: "600",
          display: "flex", alignItems: "center", gap: "6px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)", animation: "pulse 2s infinite"
        }}>
          <span style={{ fontSize: "14px" }}>Bilgi:</span> Akıllı Önbellek Hazırlanıyor...
        </div>
      )}

    </>
  );
}
/* === MAP_SECTION END === */

